import "./CaseShowcase.css";

import { ArrowRight, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import { useOnScrollReveal } from "@/4-components/animations/anime";
import { useBarCarousel } from "@/4-components/animations/carousel";
import { CarouselBars } from "@/4-components/animations/CarouselBars";
import { Collapse } from "@/4-components/primitives/Collapse";
import { DiagonalPhoto } from "@/4-components/primitives/DiagonalPhoto";
import { SectionHead } from "@/4-components/primitives/SectionHead";
import type { CaseItem } from "@/5-shared/constants/content/types";
import { Link } from "@/5-shared/routing";

const CYCLE_MS = 4000;

/** Rotates expandable cases while keeping direct controls and collapsed content accessible. */
export function CaseShowcase({ eyebrow, heading, sub, items }: { eyebrow: string; heading: string; sub?: string; items: CaseItem[] }) {
  const ref = useOnScrollReveal<HTMLElement>();
  const [inView, setInView] = useState(false);
  const { i: openIdx, running, pick, next } = useBarCarousel(items.length);

  useEffect(() => {
    const section = ref.current;
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => {
      setInView(!!entry?.isIntersecting);
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, [ref]);

  return (
    <section ref={ref} className="case-showcase section bg-zinc-50">
      <div className="shell">
        <SectionHead eyebrow={eyebrow} heading={heading} sub={sub} reveal className="section-head--centered max-w-none" />

        <div data-carousel>
          <div data-reveal data-running={running && inView} className="case-showcase__timeline mb-5 transition-opacity duration-300">
            <CarouselBars
              items={items.map((c) => c.title)}
              index={openIdx}
              running={running && inView}
              cycleMs={CYCLE_MS}
              onPick={pick}
              onCycleEnd={next}
              label={heading}
              className="carousel-bars--progress"
            />
          </div>

          <div className="space-y-5">
            {items.map((c, i) => {
              const Icon = c.icon;
              const open = openIdx === i;
              return (
                <article
                  data-reveal
                  data-open={open}
                  key={c.title}
                  className="case-showcase__item group relative overflow-hidden card transition-all duration-300"
                >
                  <DiagonalPhoto src={c.image} srcSet={c.imageSrcSet} open={open} mirror={c.mirror} />

                  <div className="relative">
                    <h3>
                      <button
                        onClick={() => pick(i)}
                        aria-expanded={open}
                        aria-controls={`case-panel-${i}`}
                        className="case-showcase__trigger w-full flex items-start md:items-center gap-4 text-left transition-all duration-300"
                      >
                        <span className="flex flex-col items-start gap-2 min-w-0 flex-1 md:flex-row md:items-center md:gap-4">
                          <span
                            className="case-showcase__icon flex items-center justify-center shrink-0 rounded-2xl transition-all duration-300"
                            aria-hidden="true"
                          >
                            <Icon />
                          </span>
                          <span className="case-showcase__title min-w-0 card-title">{c.title}</span>
                        </span>
                        <Plus className="case-showcase__plus w-5 h-5 shrink-0 ml-auto text-dec-green" aria-hidden="true" />
                      </button>
                    </h3>
                    <Collapse open={open} id={`case-panel-${i}`} className="md:max-w-[62%]">
                      <div className="px-5 md:px-8 pb-6 md:pb-8 pt-1">
                        <p className="text-zinc-600 md:text-lg">{c.body}</p>
                        <Link to={c.solution.to} className="group/text-link text-link mt-6">
                          {c.solution.label}
                          <ArrowRight className="w-4 h-4 transition-transform group-hover/text-link:translate-x-1" />
                        </Link>
                      </div>
                    </Collapse>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
